import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {SLIDES_URL} from "shared/api/urls";
import {baseApiInstanceWithRefresh} from "../../api/baseApiInstance.js";
import {getSlideById} from "../../api/getSlideById.js";
import {getFavorites, postFavorite, deleteFavorite} from "../../api/favorites.js";

export const fetchSlidesByPage = createAsyncThunk(
    'slides/fetchSlidesByPage',
    (requestParams) => {
        const queryParams = new URLSearchParams({
            page: requestParams.page,
            search: requestParams.search,
            ordering: requestParams.ordering,
        });

        return baseApiInstanceWithRefresh
            .get(`${SLIDES_URL}?${queryParams.toString()}`)
            .then(data => data.data)
    })

export const fetchSlideById = createAsyncThunk(
    'slides/fetchSlideById',
    async (slideId) => {
        return await getSlideById(slideId);
    }
)

export const fetchFavorites = createAsyncThunk(
    'slides/fetchFavorites',
    async () => {
        return await getFavorites();
    }
)

export const addFavoriteAsync = createAsyncThunk(
    'slides/addFavoriteAsync',
    async (slideData) => {
        await postFavorite(slideData.id);
        return slideData;
    }
)

export const removeFavoriteAsync = createAsyncThunk(
    'slides/removeFavoriteAsync',
    async (slideId) => {
        await deleteFavorite(slideId);
        return slideId;
    }
)

// Функция для загрузки просмотренных слайдов из localStorage
const loadViewedSlidesFromLocalStorage = () => {
    try {
        const stored = localStorage.getItem('viewedSlides');
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Ошибка при загрузке просмотренных слайдов из localStorage:', e);
        return [];
    }
};

const initialState = {
    loading: false,
    isError: false,
    error: '',
    data: [],
    selected: [],
    // Параметры последнего запроса списка — нужны, чтобы обновить таблицу
    // после массовых операций (например, удаления)
    requestParams: {page: 1, search: '', ordering: '-id'},
    viewedSlides: loadViewedSlidesFromLocalStorage(),
    favoriteSlides: [],
    favoritesLoading: false,
};

export const slidesSlice = createSlice({
    name: "slides",
    initialState: initialState,
    reducers: {
        addSelected: (state, action) => {
            if (!state.selected.some((item) => item.id === action.payload.id)) {
                state.selected.push(action.payload);
            }
        },
        removeSelected: (state, action) => {
            state.selected = [...state.selected.filter((item) => item.id !== action.payload)];
        },
        // Отметить сразу несколько слайдов (чекбокс «выделить все на странице»)
        addSelectedSlides: (state, action) => {
            const selectedIds = state.selected.map((item) => item.id);
            const newSlides = action.payload.filter(
                (slide) => !selectedIds.includes(slide.id));

            state.selected = [...state.selected, ...newSlides];
        },
        // Снять отметку сразу с нескольких слайдов
        removeSelectedSlides: (state, action) => {
            const removedIds = action.payload;

            state.selected = state.selected.filter(
                (item) => !removedIds.includes(item.id));
        },
        removeAllSelected: (state) => {
            // Чекбоксы строк выводятся из selected, поэтому очистки списка
            // достаточно, чтобы снялись все отметки
            state.selected = [];
        },
        removeSlides: (state, action) => {
            const removedIds = action.payload;

            state.selected = state.selected.filter(
                (slide) => !removedIds.includes(slide.id));
            state.favoriteSlides = state.favoriteSlides.filter(
                (slide) => !removedIds.includes(slide.id));
            state.viewedSlides = state.viewedSlides.filter(
                (slideId) => !removedIds.includes(slideId));

            try {
                localStorage.setItem(
                    'viewedSlides', JSON.stringify(state.viewedSlides));
            } catch (e) {
                // Ошибка при сохранении в localStorage
            }

            if (state.data.results) {
                state.data.results = state.data.results.filter(
                    (slide) => !removedIds.includes(slide.id));
            }
        },
        markSlideAsViewed: (state, action) => {
            const slideId = action.payload;
            if (!state.viewedSlides.includes(slideId)) {
                state.viewedSlides.push(slideId);
                try {
                    localStorage.setItem('viewedSlides', JSON.stringify(state.viewedSlides));
                } catch (e) {
                    // Ошибка при сохранении в localStorage
                }
            }
        },
        loadViewedSlidesFromStorage: (state) => {
            state.viewedSlides = loadViewedSlidesFromLocalStorage();
        },
        updateSlideStatus: (state, action) => {
            const updatedSlide = action.payload;
            if (state.data.results) {
                const slideIndex = state.data.results.findIndex(slide => slide.id === updatedSlide.id);
                if (slideIndex !== -1) {
                    state.data.results[slideIndex] = {...state.data.results[slideIndex], ...updatedSlide};
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSlidesByPage.pending, (state, action) => {
                state.loading = true
                state.requestParams = action.meta.arg
            })
            .addCase(fetchSlidesByPage.fulfilled, (state, action) => {
                state.loading = false
                state.isError = false
                state.data = action.payload
                state.error = ''
            })
            .addCase(fetchSlidesByPage.rejected, (state, action) => {
                state.loading = false
                state.isError = true
                state.data = []
                state.error = action.error.message
            })
            .addCase(fetchSlideById.fulfilled, (state, action) => {
                const updatedSlide = action.payload;
                if (state.data.results) {
                    const slideIndex = state.data.results.findIndex(slide => slide.id === updatedSlide.id);
                    if (slideIndex !== -1) {
                        state.data.results[slideIndex] = {...state.data.results[slideIndex], ...updatedSlide};
                    }
                }
            })
            // Избранное
            .addCase(fetchFavorites.pending, (state) => {
                state.favoritesLoading = true;
            })
            .addCase(fetchFavorites.fulfilled, (state, action) => {
                state.favoritesLoading = false;
                state.favoriteSlides = action.payload;
            })
            .addCase(fetchFavorites.rejected, (state) => {
                state.favoritesLoading = false;
            })
            .addCase(addFavoriteAsync.fulfilled, (state, action) => {
                const slide = action.payload;
                if (!state.favoriteSlides.some(s => s.id === slide.id)) {
                    state.favoriteSlides.unshift(slide);
                }
            })
            .addCase(removeFavoriteAsync.fulfilled, (state, action) => {
                const slideId = action.payload;
                state.favoriteSlides = state.favoriteSlides.filter(s => s.id !== slideId);
            })
    }
});


export const {
    addSelected,
    removeSelected,
    addSelectedSlides,
    removeSelectedSlides,
    removeAllSelected,
    removeSlides,
    markSlideAsViewed,
    loadViewedSlidesFromStorage,
    updateSlideStatus,
} = slidesSlice.actions;

export default slidesSlice.reducer;
